import { Component } from '@angular/core';
import { RouterLink } from "@angular/router";
import {
  AnnualStatementPageFilter,
  AnnualStatementStatus,
  SigningStatus
} from "@models/annual-statements/annual-statement-enums";
import { AnnualStatementRecord } from "@models/annual-statements/annual-statement-record";
import { ApproverStatus } from '@models/documents/approver-status.enum';
import { SubAnnualMenuItem, SubMenuItem } from '@models/ui-models/section-form-menu';
import { NgbDropdown, NgbDropdownItem, NgbDropdownMenu, NgbDropdownToggle } from "@ng-bootstrap/ng-bootstrap";
import {
  AgActionIconButtonComponent
} from "@shared/components/grid/ag-cells/common/ag-action-icon-button/ag-action-icon-button.component";
import { HasRoleDirective } from '@shared/directives/has-role.directive';
import { Guid } from "@shared/utils/guid.helper";
import { getAnnualStatementsPath, getCompanyProfilePath } from "@shared/utils/route-path-utils";
import { ICellRendererAngularComp } from "ag-grid-angular";
import { ICellRendererParams } from "ag-grid-community";

type ActionEvent = (data: unknown) => void;

type CellRendererParams = ICellRendererParams<AnnualStatementRecord> & {
  pageFilter: AnnualStatementPageFilter;
  send: ActionEvent,
  downloadDocuments: ActionEvent,
  archive: ActionEvent,
  ignore: ActionEvent,
  sendReminder: ActionEvent,
  sendPaymentReminder: ActionEvent,
  regenerate: ActionEvent,
  markAsSent: ActionEvent,
  markAsSigned: ActionEvent
  refreshCompanyDebt: ActionEvent,
  reprintAS: ActionEvent,
  downloadCombinedPDF: ActionEvent,
  requestRefresh: ActionEvent,//???
};

@Component({
  selector: 'app-ag-annual-actions',
  standalone: true,
  imports: [
    NgbDropdown,
    NgbDropdownItem,
    NgbDropdownMenu,
    NgbDropdownToggle,
    HasRoleDirective,
    AgActionIconButtonComponent,
    RouterLink
  ],
  templateUrl: 'ag-annual-actions.component.html',
  styleUrl: 'ag-annual-actions.component.scss'
})
export class AgAnnualActionsComponent implements ICellRendererAngularComp {
  readonly AnnualStatementStatus = AnnualStatementStatus;
  readonly AnnualStatementPageFilter = AnnualStatementPageFilter;
  readonly SigningStatus = SigningStatus;

  params!: CellRendererParams;
  pageFilter: AnnualStatementPageFilter = AnnualStatementPageFilter.All;
  status: AnnualStatementStatus | null | undefined;
  signingStatus: SigningStatus | undefined;
  approverStatus: ApproverStatus | undefined;
  isPaid!: boolean;
  documentId: string | null | undefined;
  debt: number | undefined;
  menu: SubMenuItem[] = [];
  annualStatementEditPath: string[] | null = null;
  companyProfilePath: string[] | null = null;
  menuActions: SubAnnualMenuItem[] = [];

  // =====  Initialising  =====

  agInit(params: CellRendererParams): void {
    this.setParams(params);
  }

  refresh(params: CellRendererParams): boolean {
    this.setParams(params);
    return true;
  }

  setParams(params: CellRendererParams): void {
    this.params = params;
    this.status = params.data?.status;
    this.signingStatus = params.data?.signingStatus;
    this.approverStatus = params.data?.approverStatus;
    this.isPaid = params.data?.isPaid ?? false;
    this.documentId = params.data?.documentId;
    this.debt = params.data?.debt ?? 0;
    this.annualStatementEditPath = this.getAnnualStatementEditPath();
    this.companyProfilePath = this.params.data?.companyId ? getCompanyProfilePath(this.params.data?.companyId) : null;

    this.setMenuActions();
  }

  setMenuActions(): void {
    if (this.pageFilter === AnnualStatementPageFilter.Expected) {
      this.menuActions = [];
      return;
    }

    const menuActions = [
      {
        title: 'Re-generate',
        iconClass: 'icon ic-refresh-20 mr-1',
        isVisible: () => this.getIsRegenerateVisible(),
        action: () => this.regenerate()
      },
      {
        title: 'Mark as Sent',
        iconClass: 'icon ic-checkmark-circle-20 mr-1',
        isVisible: () => this.getIsMarkAsSentVisible(),
        action: () => this.markAsSent()
      },
      {
        title: 'Mark as Signed',
        iconClass: 'icon ic-checkmark-circle-20 mr-1',
        isVisible: () => this.getIsMarkAsSignedVisible(),
        action: () => this.markAsSigned()
      },
      {
        title: 'Send ad-hoc Signing Reminder',
        iconClass: 'icon ic-bell-20 mr-1',
        isVisible: () => this.getIsAdHocSigningVisible(),
        action: () => this.sendReminder()
      },
      {
        title: 'Send ad-hoc Payment Reminder',
        iconClass: 'icon ic-bell-20 mr-1',
        isVisible: () => this.getIsAdHocPaymentVisible(),
        action: () => this.sendPaymentReminder()
      },
      {
        title: 'Refresh Debt via RA63',
        iconClass: 'icon ic-refresh-20 mr-1',
        isVisible: () => this.getIsRefreshDebtVisible(),
        action: () => this.refreshCompanyDebt()
      },
      {
        title: 'Ignore',
        pages: [AnnualStatementPageFilter.NotReceived, AnnualStatementPageFilter.All],
        iconClass: 'icon ic-download-20 mr-1',
        isVisible: () => this.getIsIgnoreVisible(),
        action: () => this.ignore()
      },
      {
        title: 'Archive',
        pagesExclude: [AnnualStatementPageFilter.NotReceived, AnnualStatementPageFilter.Expected],
        iconClass: 'icon ic-archive-20 mr-1',
        isVisible: () => this.getIsArchiveVisible(),
        action: () => this.archive()
      },
      {
        title: 'Download Documents',
        pagesExclude: [AnnualStatementPageFilter.NotReceived, AnnualStatementPageFilter.Expected],
        iconClass: 'icon ic-download-20 mr-1',
        isVisible: () => this.getIsDownloadDocumentsVisible(),
        action: () => this.downloadDocuments()
      },
      {
        title: 'Download combined PDF',
        pagesExclude: [AnnualStatementPageFilter.NotReceived, AnnualStatementPageFilter.Expected],
        iconClass: 'icon ic-download-20 mr-1',
        isVisible: () => this.getIsDownloadDocumentsVisible(),
        action: () => this.downloadCombinedPDF()
      },
    ];

    this.menuActions = menuActions.filter(item => {
      const pages = item.pages ?? [];
      const pagesExclude = item.pagesExclude ?? [];

      const inPages = pages.length === 0 || pages.includes(this.pageFilter);
      const notExcluded = !pagesExclude.includes(this.pageFilter);

      const visible = typeof item.isVisible === 'function'
        ? item.isVisible()
        : item.isVisible !== false;

      return inPages && notExcluded && visible;
    });
  }

  getAnnualStatementEditPath(): string[] | null {
    return this.documentId
    && Guid.isValidGuid(this.documentId)
    && !this.isCustomiseAndSendDisabled
      ? getAnnualStatementsPath(this.documentId)
      : null;
  }

  // =====  Actions  =====

  send(): void {
    if (this.isViewAndSendDisabled) return;
    this.params.send(this.params.data?.documentId);
  }

  downloadDocuments(): void {
    this.params.downloadDocuments(this.params.data);
  }

  downloadCombinedPDF(): void {
    this.params.downloadCombinedPDF(this.params.data);
  }

  archive(): void {
    this.params.archive(this.params.data);
  }

  sendReminder(): void {
    this.params.sendReminder(this.params.data?.companyId);
  }

  sendPaymentReminder(): void {
    this.params.sendPaymentReminder(this.params.data?.companyId);
  }

  refreshCompanyDebt(): void {
    this.params.refreshCompanyDebt(this.params.data?.companyId);
  }

  refreshCompanyInfo(): void {
    this.params.reprintAS(this.params.data?.companyId);
  }

  regenerate(): void {
    this.params.regenerate(this.params.data);
  }

  markAsSent(): void {
    this.params.markAsSent(this.params.data);
  }

  markAsSigned(): void {
    this.params.markAsSigned(this.params.data);
  }

  ignore(): void {
    this.params.ignore(this.params.data?.companyId);
  }

  reprint(): void {
    this.params.reprintAS(this.params.data?.companyId);
  }

  // =====  Visibility rules  =====

  getIsDownloadDocumentsVisible(): boolean {
    return !!this.documentId && Guid.isValidGuid(this.documentId) && this.status !== AnnualStatementStatus.ReadyToPrepare;
  }

  getIsRegenerateVisible(): boolean {
    return this.status === AnnualStatementStatus.ReadyToSend || this.status === AnnualStatementStatus.NotReceived || this.status === AnnualStatementStatus.ReadyToPrepare;
  }

  getIsMarkAsSentVisible(): boolean {
    return this.status === AnnualStatementStatus.ReadyToSend || this.signingStatus === SigningStatus.SentViaEmail;
  }

  getIsMarkAsSignedVisible(): boolean {
    return (this.status === AnnualStatementStatus.ReadyToSend || this.status === AnnualStatementStatus.Sent || this.isPaid) && this.signingStatus !== SigningStatus.MarkedAsSigned;
  }

  getIsIgnoreVisible(): boolean {
    return this.status === AnnualStatementStatus.NotReceived;
  }

  getIsArchiveVisible(): boolean {
    return this.status !== AnnualStatementStatus.Archived && this.status !== AnnualStatementStatus.NotReceived && this.status !== AnnualStatementStatus.Expected;
  }

  getIsAdHocPaymentVisible(): boolean {
    return (this.status === AnnualStatementStatus.Sent || this.status === AnnualStatementStatus.Signed) && (!!this.debt && this.debt > 0) && !this.isPaid;
  }

  getIsAdHocSigningVisible(): boolean {
    return this.status === AnnualStatementStatus.Sent && this.signingStatus === SigningStatus.Sent;
  }

  getIsRefreshDebtVisible(): boolean {
    return this.isPaid || this.status === AnnualStatementStatus.Sent || this.status === AnnualStatementStatus.Signed;
  }

  get isViewAndSendDisabled() {
    return this.status === null
      || this.documentId === null
      || [
        AnnualStatementStatus.ReadyToPrepare,
        AnnualStatementStatus.NotReceived,
        AnnualStatementStatus.Expected,
      ].includes(this.status!)
      || (this.signingStatus !== SigningStatus.ReadyToSend && this.signingStatus !== SigningStatus.Created);
  }

  get isCustomiseAndSendDisabled() {
    return this.status === null
      || this.documentId === null
      || [
        AnnualStatementStatus.NotReceived,
        AnnualStatementStatus.Expected,
      ].includes(this.status!);
  }
}
